import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Smartphone, Search, Megaphone, Globe, BarChart3, Shield } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

const ServicesSection = () => {
  const services = [
    {
      icon: Globe,
      title: "Web Development",
      description:
        "Custom web applications and websites built with modern technologies. Responsive, fast, and scalable solutions for your business.",
      image: "assets/img/services/webDev.jpg",
      link: "/contactUs",
      gradient: "from-blue-500/20 to-cyan-500/20",
    },
    {
      icon: Smartphone,
      title: "Mobile Application Development",
      description:
        "High-performance mobile apps for iOS and Android platforms, tailored to your business needs and user expectations.",
      image: "assets/img/services/appDev.jpg",
      link: "/contactUs",
      gradient: "from-purple-500/20 to-pink-500/20",
    },
    {
      icon: Search,
      title: "Search Engine Optimization (SEO)",
      description:
        "Improve your website's visibility on search engines through effective SEO strategies to drive organic traffic and growth.",
      image: "assets/img/services/Seo.jpg",
      link: "/contactUs",
      gradient: "from-green-500/20 to-emerald-500/20",
    },
    {
      icon: Megaphone,
      title: "Digital Marketing",
      description:
        "Boost your online presence through targeted digital campaigns including social media, paid ads, email marketing, and more.",
      image: "assets/img/services/digitalMarketing.jpg",
      link: "/contactUs",
      gradient: "from-orange-500/20 to-red-500/20",
    },
    {
      icon: BarChart3,
      title: "Business Analysis",
      description:
        "Comprehensive business analysis services to help you make data-driven decisions and optimize your operations.",
      image: "assets/img/services/BusinessAnalysis.jpg",
      link: "/contactUs",
      gradient: "from-indigo-500/20 to-blue-500/20",
    },
    {
      icon: Shield,
      title: "IT Support & Maintenance",
      description:
        "Reliable support and maintenance to ensure your IT systems run smoothly. Proactive monitoring and troubleshooting.",
      image: "assets/img/services-1.jpg",
      link: "/contactUs",
      gradient: "from-teal-500/20 to-cyan-500/20",
    },
  ];

  return (
    <section id="services" className="py-16 md:py-24 lg:py-32 bg-muted/30">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16 space-y-4">
          <div className="inline-block">
            <span className="text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-full">
              Our Services
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
            Comprehensive IT{" "}
            <span className="text-primary">Solutions</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            We offer a wide range of IT services to help your business thrive in the digital age.
            From web development to digital marketing, we've got you covered.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Card
                key={index}
                className="group overflow-hidden border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl"
              >
                <div className="relative h-48 overflow-hidden">
                  <div
                    className={cn(
                      "absolute inset-0 bg-gradient-to-br opacity-50 group-hover:opacity-70 transition-opacity",
                      service.gradient
                    )}
                  />
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <div className="h-12 w-12 rounded-lg bg-background/90 backdrop-blur-sm flex items-center justify-center shadow-lg">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <CardDescription className="text-base leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="ghost" asChild className="group/btn">
                    <Link to={service.link}>
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Button size="lg" asChild>
            <Link to="/contactUs">
              View All Services
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
